import '@fontsource-variable/inter'
import '~/assets/index.css'
import { initApp, router } from '~/boot'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '~/stores/auth/AuthStore'
import { useSettingsStore } from '~/stores/SettingsStore'
import { useFeatureFlagsStore } from '~/stores/featureFlagsStore'
import { hasPermission } from '~/utils/permissions'
import { isDisabledByFeatureFlag, shouldRedirectAuthenticatedToHome } from '~/utils/route-access'

// Initialize app asynchronously
initApp.then((app) => {
  app.provide('$can', hasPermission)
  app.config.globalProperties.$can = hasPermission

  app.mount('#app')
})

/**
 * Navigation Guard
 */
router.beforeEach(
  async (to: RouteLocationNormalized, _: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (!to.matched.length) {
      next({ name: '404', params: { all: to.path.replace(/^\//, '') } })
      return
    }
    const authStore = useAuthStore()
    const settingsStore = useSettingsStore()

    if (to.matched.some((record) => record.meta.requiresAuth)) {
      if (!authStore.isLoggedIn) {
        localStorage.setItem('redirectUrl', to.fullPath)
        next('/auth/login')
        return
      }

      // /feature-flags is auth-gated; fetching as a guest guarantees a 401,
      // so flags load on the first authenticated navigation instead of at boot.
      const featureFlagsStore = useFeatureFlagsStore()
      if (!featureFlagsStore.loaded) {
        await featureFlagsStore.fetchFlags()
      }

      if (isDisabledByFeatureFlag(to, featureFlagsStore.isEnabled)) {
        next('/my-calendar')
        return
      }

      // Legacy /chat redirect — feature has been replaced by /messaging.
      if (to.path === '/chat') {
        next('/messaging')
        return
      }
    } else if (shouldRedirectAuthenticatedToHome(to, authStore.isLoggedIn)) {
      next('/')
      return
    }

    next()
  },
)
